import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

import { projects } from '@/constants/content'
import { SplitText } from '@/components/motion/split-text'
import { Reveal } from '@/components/motion/reveal'
import { RevealImage } from '@/components/motion/reveal-image'

const featured = projects.slice(0, 4)

export const SelectedWork: FC = (): ReactNode => {
    return (
        <section
            id='work'
            className='edge bg-background py-20 md:py-28'
            aria-labelledby='work-heading'
        >
            <div className='mx-auto max-w-[110rem]'>
                <p className='label mb-10 text-muted-foreground'>Portfolio</p>

                <div className='grid gap-8 lg:grid-cols-2 lg:items-end lg:gap-20'>
                    <SplitText
                        as='h2'
                        lines={['See the magic', 'for yourself']}
                        className='display-xl'
                    />

                    <span id='work-heading' className='sr-only'>
                        Selected work
                    </span>

                    <Reveal delay={0.1} className='space-y-5 lg:pb-3'>
                        <p className='max-w-sm text-[0.9375rem] leading-relaxed text-foreground/80'>
                            Browse a curated collection of moments that have made their way
                            into the hearts of the people in them.
                        </p>

                        <p className='max-w-sm text-[0.8125rem] leading-relaxed text-muted-foreground'>
                            Natural, unhurried and story-led. The aim is always to photograph
                            the real you rather than a version arranged for the camera.
                        </p>
                    </Reveal>
                </div>

                <ul className='mt-14 grid gap-x-4 gap-y-10 md:mt-18 md:grid-cols-2'>
                    {featured.map((project, index) => (
                        <li key={project.slug}>
                            <Link href={`/work/${project.slug}`} className='group block'>
                                <RevealImage
                                    src={project.image}
                                    alt={project.title}
                                    className='aspect-[4/3] rounded-[var(--radius-lg)]'
                                    imageClassName='transition-transform duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]'
                                    sizes='(max-width: 768px) 100vw, 50vw'
                                    delay={(index % 2) * 0.1}
                                    parallax={index % 2 === 0 ? 10 : 18}
                                />

                                <div className='mt-4 flex items-baseline justify-between gap-6'>
                                    <span>
                                        <span className='block text-[0.9375rem] font-medium transition-colors duration-500 group-hover:text-[color:var(--gold-ink)]'>
                                            {project.title}
                                        </span>

                                        <span className='mt-0.5 block text-[0.75rem] text-muted-foreground'>
                                            {project.category}
                                        </span>
                                    </span>

                                    <span className='shrink-0 text-[0.75rem] tabular-nums text-muted-foreground'>
                                        {project.date}
                                    </span>
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>

                <Reveal className='mt-14' delay={0.08}>
                    <Link
                        href='/portfolio'
                        className='pill border border-border hover:bg-foreground hover:text-background'
                    >
                        View the full portfolio
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>
                </Reveal>
            </div>
        </section>
    )
}
